import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Heading2, Heading3, Heading4 } from '@shopmuchmore/design-system';
import { ArrowRight } from 'lucide-react';

interface CategoryItem {
  id: string;
  image: string;
  headline: string;
  cta_text: string;
  url: string;
}

const CATEGORY_BENTO_DATA: CategoryItem[] = [
  {
    id: 'bento_1',
    image: '/images/bento/flavours.webp',
    headline: 'Flavours of Home',
    cta_text: 'Shop Spices & Grocery',
    url: '/browse?slug=food-grocery',
  },
  {
    id: 'bento_2',
    image: '/images/bento/beauty.jpg',
    headline: 'Beauty & Wellness',
    cta_text: 'Shop Natural Beauty',
    url: '/browse?slug=beauty-wellness-personal-care',
  },
  {
    id: 'bento_3',
    image: '/images/bento/fashion.webp',
    headline: 'Heritage & Threads',
    cta_text: 'Explore Cultural Fashion',
    url: '/browse?slug=fashion-traditional-wear',
  },
  {
    id: 'bento_4',
    image: '/images/bento/gifts.jpg',
    headline: 'Thoughtful & Cultural Gifts',
    cta_text: 'Shop Cultural Gifts',
    url: '/browse?slug=gifts-occasions',
  },
];

interface BentoCardProps {
  item: CategoryItem;
  variant: 'large' | 'wide' | 'small';
  onItemClick: (item: CategoryItem) => void;
}

function BentoCard({ item, variant, onItemClick }: BentoCardProps) {
  const handleCtaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onItemClick(item);
  };

  const getCardClasses = () => {
    const base =
      'relative rounded-3xl overflow-hidden cursor-pointer group border-[1.5px] border-gray-100 transition-all duration-300 hover:scale-[1.02]';

    switch (variant) {
      case 'large':
        return `${base} col-span-1 aspect-square md:row-span-2 md:h-auto md:aspect-auto`;
      case 'wide':
        return `${base} col-span-1 aspect-square md:col-span-2 md:h-auto md:aspect-auto`;
      case 'small':
      default:
        return `${base} col-span-1 aspect-square md:h-auto md:aspect-auto`;
    }
  };

  const getGradientClasses = () => {
    switch (variant) {
      case 'wide':
        return 'bg-gradient-to-r from-black/70 via-black/40 to-black/20';
      default:
        return 'bg-gradient-to-t from-black/70 via-black/30 to-black/10';
    }
  };

  const HeadingComponent = variant === 'large' ? Heading2 : variant === 'wide' ? Heading3 : Heading4;
  const headingClasses =
    variant === 'large'
      ? 'text-xl md:text-3xl lg:text-4xl text-white mb-2 md:mb-3'
      : variant === 'wide'
        ? 'text-lg md:text-3xl text-white mb-2 md:mb-3'
        : 'text-lg md:text-2xl text-white mb-2';
  const paddingClasses = variant === 'small' ? 'p-3 sm:p-4 md:p-6' : 'p-3 sm:p-4 md:p-8';

  return (
    <div onClick={() => onItemClick(item)} className={getCardClasses()}>
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
        style={{ backgroundImage: `url(${item.image})` }}
      />
      <div className={`absolute inset-0 ${getGradientClasses()}`} />
      <div className={`relative h-full flex flex-col justify-end ${paddingClasses}`}>
        <HeadingComponent className={headingClasses}>{item.headline}</HeadingComponent>
        {item.cta_text && (
          <Button
            size="sm"
            onClick={handleCtaClick}
            className="w-fit gap-1 bg-white/90 text-gray-900 hover:bg-white hover:text-pink-600 transition-colors text-[10px] sm:text-xs md:text-sm h-7 sm:h-9 px-2 sm:px-4 rounded-lg md:rounded-xl"
          >
            <span className="inline sm:hidden">
              {item.cta_text.replace(/^(Shop|Explore)\s+/i, '')}
            </span>
            <span className="hidden sm:inline">{item.cta_text}</span>
            <ArrowRight className="size-2.5 sm:size-3 md:size-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function CategoryBentoGrid() {
  const navigate = useNavigate();

  const handleItemClick = (item: CategoryItem) => {
    navigate(item.url);
  };

  const [largeLeft, wideTopRight, smallBottomLeft, smallBottomRight] = CATEGORY_BENTO_DATA;

  return (
    <section className="w-full max-w-7xl mx-auto py-8 md:py-12">
      <div className="grid grid-cols-2 md:grid-cols-3 md:grid-rows-2 gap-3 md:gap-6">
        <BentoCard item={largeLeft} variant="large" onItemClick={handleItemClick} />
        <BentoCard item={wideTopRight} variant="wide" onItemClick={handleItemClick} />
        <BentoCard item={smallBottomLeft} variant="small" onItemClick={handleItemClick} />
        <BentoCard item={smallBottomRight} variant="small" onItemClick={handleItemClick} />
      </div>
    </section>
  );
}
